import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { LOCAL_PAGES } from '@/lib/localPages';
import DimensionLine from '@/components/DimensionLine';
import BuildingGlyph from '@/components/BuildingGlyph';
import type { GlyphType } from '@/components/BuildingGlyph';

/** The asset type each service is drawn with, in service.config order. */
const SERVICE_GLYPHS: GlyphType[] = ['office', 'retail', 'industrial'];

export default function Home() {
  useTitle('', site.brand.description);

  return (
    <>
      {/* ── A-00 · Cover sheet ────────────────────────────────────────────
          Cyanotype, full bleed, with the parcel drawn at large scale. */}
      <section className="relative overflow-hidden bg-blueprint bg-grid-blueprint text-white">
        <span className="sheet-no absolute right-5 top-5 text-white/35 sm:right-8">A-00</span>

        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
            <div className="animate-fade-in">
              <DimensionLine
                label={site.serviceAreas.map((a) => a.name).join(' · ')}
                inverse
                className="max-w-lg"
              />

              <h1 className="mt-8 font-display text-[46px] sm:text-[72px] font-semibold leading-[0.98] tracking-tight">
                Commercial real estate,
                <br />
                handled directly.
              </h1>

              <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-blueprint-100/85">
                {site.brand.description}
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary">Start a conversation</Link>
                <Link to="/about" className="btn-ghost-inverse">About Pinnacle Commercial</Link>
              </div>
            </div>

            {/* Drawn, not photographed — see BuildingGlyph. */}
            <div className="hidden lg:block">
              <BuildingGlyph type="land" className="w-full text-white/45" />
              <p className="annot-inverse mt-5 text-right">Fig. 1 — Parcel, typical</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Drawing index ─────────────────────────────────────────────────
          The three services as an index of sheets. A numbered index is how a
          drawing set lists its contents, and it lets each service carry real
          typographic weight instead of being one of three equal cards. */}
      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Sheet index — what we do" className="mb-14" />

          {site.services.map((s, i) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              className="group grid grid-cols-[auto_1fr] items-start gap-x-6 gap-y-4 border-t border-hairline
                         py-9 transition-colors last:border-b last:border-hairline hover:bg-table/60
                         sm:grid-cols-[auto_1fr_auto] sm:gap-x-10"
            >
              <span className="font-display text-[30px] leading-none text-medline transition-colors group-hover:text-redline sm:text-[38px]">
                0{i + 1}
              </span>

              <div className="min-w-0">
                <p className="annot">{s.short}</p>
                <h2 className="mt-2 font-display text-[28px] leading-tight sm:text-[34px]">{s.name}</h2>
                <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-annot">{s.summary}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider2 text-graphite">
                  Open sheet
                  <svg
                    width="18"
                    height="8"
                    viewBox="0 0 18 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="transition-transform duration-200 group-hover:translate-x-1.5"
                  >
                    <path d="M0 4h16M13 1l3 3-3 3" />
                  </svg>
                </span>
              </div>

              <BuildingGlyph
                type={SERVICE_GLYPHS[i]}
                className="col-start-2 w-36 self-center text-medline transition-colors
                           group-hover:text-blueprint sm:col-start-3 sm:w-44"
              />
            </Link>
          ))}
        </div>
      </section>

      {/* ── Area schedule ─────────────────────────────────────────────────
          A schedule table, the way a drawing set tabulates rooms or units —
          deliberately not another card grid. */}
      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Area schedule — where we work" className="mb-12" />

          <div className="border border-medline">
            <div className="hidden bg-blueprint px-5 py-3 sm:grid sm:grid-cols-[200px_1fr] sm:gap-8">
              <span className="annot-inverse">City</span>
              <span className="annot-inverse">Commercial character</span>
            </div>

            {site.serviceAreas.map((a, i) => (
              <div
                key={a.slug}
                className={`grid gap-2 bg-sheet px-5 py-6 sm:grid-cols-[200px_1fr] sm:gap-8 ${
                  i > 0 ? 'border-t border-hairline' : ''
                }`}
              >
                <div className="flex items-baseline gap-3">
                  <span className="sheet-no">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-display text-[22px] leading-none">{a.name}</h3>
                </div>
                <p className="text-[15px] leading-relaxed text-annot">{a.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Local sheets ──────────────────────────────────────────────────
          A second, narrower index — one sheet per city + service, for the
          specific searches the three main services don't title-match. */}
      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Local sheets — by city & service" className="mb-12" />

          <div className="grid gap-px border border-medline bg-medline sm:grid-cols-2">
            {LOCAL_PAGES.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="group flex items-start gap-5 bg-sheet px-6 py-6 transition-colors hover:bg-table/60"
              >
                <span className="sheet-no pt-0.5 group-hover:text-redline">{p.sheetNo}</span>
                <div>
                  <p className="font-display text-[17px] leading-snug">{p.label}</p>
                  <p className="mt-1 text-[13px] text-annot">{p.area}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who you're working with ───────────────────────────────────────
          Asymmetric, with the identifying data set as margin annotation. */}
      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="grid gap-12 lg:grid-cols-[300px_1fr]">
            <div>
              <p className="annot">Prepared by</p>
              <h2 className="mt-3 font-display text-[32px] leading-tight">{site.agent.name}</h2>
              <dl className="mt-6 space-y-3 border-t border-hairline pt-5">
                <div>
                  <dt className="annot">Title</dt>
                  <dd className="mt-1 text-[14px]">{site.agent.title}</dd>
                </div>
                <div>
                  <dt className="annot">Brokerage</dt>
                  <dd className="mt-1 text-[14px]">{site.agent.brokerage}</dd>
                </div>
              </dl>
            </div>

            <div className="space-y-5 text-[17px] leading-relaxed text-graphite-600">
              {site.agent.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <Link
                to="/about"
                className="inline-flex items-center gap-2 pt-2 font-mono text-[10px] uppercase tracking-wider2 text-redline hover:text-redline-600"
              >
                More about Pinnacle Commercial
                <svg width="18" height="8" viewBox="0 0 18 8" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M0 4h16M13 1l3 3-3 3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
